import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, Pane, useMap } from 'react-leaflet';
import * as L from 'leaflet';
import { CloudRain, Thermometer, Snowflake, Droplets, Wind, Play, Pause, Table as TableIcon, Repeat, ThermometerSun } from 'lucide-react';
import Mainloader from '../../common/Mainloader';
import CommonTable from '../../common/CommonTable';
import SatelliteImagery from '../../Homepage/Cyclone/SatelliteImagery/SatelliteImagery';
import 'leaflet/dist/leaflet.css';

const ecmwfSpatialData = process.env.REACT_APP_ECMWF_SPATIAL_DATA;

// ─────────────────────────────────────────────────────────────────────────────
// COLOR PALETTE
// Single source of truth for all alert colors used across legends, map fills,
// and stats modal. Any color change here propagates everywhere automatically.
// ─────────────────────────────────────────────────────────────────────────────
const ALERT_COLORS = {
  green:  'rgb(148, 203, 85)',   // Low / Light / No Effect / Cool / Cold
  yellow: 'rgb(243, 235, 18)',   // Moderate / Warm / Mild
  orange: 'rgb(243, 155, 58)',   // High / Heavy / Hot / Strong
  red:    'rgb(230, 45, 58)',    // Very High / Extreme / Severe
  grey:   'rgb(222, 222, 223)',  // No data / Unknown
};


const PARAM_RANGES = {
  rainfall: [
    { max: 15,       color: ALERT_COLORS.green,  label: 'Light',    value: '0–15 mm'   },
    { max: 25,       color: ALERT_COLORS.yellow, label: 'Moderate', value: '15–25 mm'  },
    { max: 60,       color: ALERT_COLORS.orange, label: 'Heavy',    value: '25–60 mm'  },
    { max: Infinity, color: ALERT_COLORS.red,    label: 'Extreme',  value: '60+ mm'    },
  ],
  max_temp: [
    { max: 20,       color: ALERT_COLORS.green,  label: 'Cool',    value: '0–20 °C'   },
    { max: 30,       color: ALERT_COLORS.yellow, label: 'Warm',    value: '20–30 °C'  },
    { max: 40,       color: ALERT_COLORS.orange, label: 'Hot',     value: '30–40 °C'  },
    { max: Infinity, color: ALERT_COLORS.red,    label: 'Extreme', value: '40+ °C'    },
  ],
  min_temp: [
    { max: 0,        color: ALERT_COLORS.red,    label: 'Cold', value: '< 0 °C'    },
    { max: 10,       color: ALERT_COLORS.orange, label: 'Cool', value: '0–10 °C'   },
    { max: 20,       color: ALERT_COLORS.yellow, label: 'Mild', value: '10–20 °C'  },
    { max: Infinity, color: ALERT_COLORS.green,  label: 'Warm', value: '20+ °C'    },
  ],
  humidity: [
    { max: 27,       color: ALERT_COLORS.green,  label: 'Low',       value: '0–27 °C'  },
    { max: 30,       color: ALERT_COLORS.yellow, label: 'Moderate',  value: '27–30 °C' },
    { max: 33,       color: ALERT_COLORS.orange, label: 'High',      value: '30–33 °C' },
    { max: Infinity, color: ALERT_COLORS.red,    label: 'Very High', value: '33+ °C'   },
  ],
  wind_speed: [
    { max: 40,       color: ALERT_COLORS.green,  label: 'Light',    value: '0–40 km/h'   },
    { max: 60,       color: ALERT_COLORS.yellow, label: 'Moderate', value: '40–60 km/h'  },
    { max: 80,       color: ALERT_COLORS.orange, label: 'Strong',   value: '60–80 km/h'  },
    { max: Infinity, color: ALERT_COLORS.red,    label: 'Severe',   value: '80+ km/h'    },
  ],
  // ── NEW: Relative Humidity ─────────────────────────────────────────────────
  relative_humidity: [
    { max: 40,       color: ALERT_COLORS.green,  label: 'Low',       value: '0–40%'  },
    { max: 60,       color: ALERT_COLORS.yellow, label: 'Moderate',  value: '40–60%' },
    { max: 80,       color: ALERT_COLORS.orange, label: 'High',      value: '60–80%' },
    { max: Infinity, color: ALERT_COLORS.red,    label: 'Very High', value: '80%+'   },
  ],
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const getColorFromRanges = (paramId, value) => {
  const ranges = PARAM_RANGES[paramId];
  if (!ranges) return ALERT_COLORS.grey;

  for (const range of ranges) {
    if (value < range.max) return range.color;
  }

  return ALERT_COLORS.grey;
};

// Helper: build the ranges array expected by the old parameters structure
const buildLegendRanges = (paramId) =>
  (PARAM_RANGES[paramId] || []).map((r) => ({
    // label: r.label,
    value: r.value,
    color: { backgroundColor: r.color },
  }));

// Leaflet only recalculates its canvas size on a real window `resize` event.
// Our responsive breakpoints (map height stepping down on mobile/tablet) and the
// sidebar hover-expand/mobile-drawer toggle change the map's *container* size
// without firing that event, so the map view stops matching its box. Watch the
// container directly and invalidate the map size whenever it changes.
const MapAutoResize = () => {
  const map = useMap();
  useEffect(() => {
    const handleResize = () => map.invalidateSize();
    handleResize();
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(map.getContainer());
    window.addEventListener('resize', handleResize);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
};

const ForecastMap = () => {
  const [activeToken, setActiveTab] = useState('spatial');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoopingTabs, setIsLoopingTabs] = useState(false);
  const [currentDay, setCurrentDay] = useState(0);
  const [currentParameterIndex, setCurrentParameterIndex] = useState(0);
  const [selectedParameter, setSelectedParameter] = useState('rainfall');
  const [hoveredParameter, setHoveredParameter] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [mapDataSM, setMapDataSM] = useState(null);
  const [mapDataSuco, setMapDataSuco] = useState(null);
  const [rainfallData, setRainfallData] = useState(null);
  const [heatIndexData, setHeatIndexData] = useState(null);
  const [windSpeedData, setWindSpeedData] = useState(null);
  const [nonSpatialData, setNonSpatialData] = useState({});
  const [forecastDates, setForecastDates] = useState([]);
  const [dataSource, setDataSource] = useState('ECMWF');
  const [loading, setLoading] = useState(false);
  const [isWeatherDataLoaded, setIsWeatherDataLoaded] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [hasDoneInitialFallback, setHasDoneInitialFallback] = useState(false);

  const [forecastUpdateDates, setForecastUpdateDates] = useState({
    rainfall: null,
    rh:       null,
    ws:       null,
  });

  const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL || '';

  const ECMWF_SPATIAL_DATA = process.env.REACT_APP_ECMWF_SPATIAL_DATA || '';

  const tabs = useMemo(() => [
    { id: 'spatial',            label: 'Spatial'               },
    { id: 'satelliteImagery',   label: 'Satellite Imagery'     },
    { id: 'suco',               label: 'Suco'                  },
    { id: 'submunicipality',    label: 'Post Administrative'   },
    { id: 'municipality',       label: 'Municipality'          },
    { id: 'valueAddedForecast', label: 'Value Added Forecast'  },
  ], []);

  const variableMap = useMemo(() => ({
    rainfall:          'tp',
    max_temp:          'heat',
    min_temp:          'cold',
    humidity:          'thi',
    relative_humidity: 'rh',
    wind_speed:        'wind',
  }), []);

  const getAdminLevel = useCallback((token) => {
    if (token === 'municipality')       return 1;
    if (token === 'submunicipality')    return 2;
    if (token === 'suco')               return 3;
    if (token === 'valueAddedForecast') return 3;
    return null;
  }, []);

  // ── Fetch ECMWF spatial update dates ──────────────────────────────────────
  useEffect(() => {
    const fetchUpdateDates = async () => {
      try {
        const response = await fetch(`${backendUrl}ecmwf/forecast_update/`);
        if (!response.ok) throw new Error('Failed to fetch update dates');
        const data = await response.json();
        const item = Array.isArray(data.forecast_update_state)
          ? data.forecast_update_state[0]
          : data.forecast_update_state;
        setForecastUpdateDates({
          rainfall: item?.ecmwf_rainfall || null,
          rh:       item?.ecmwf_rh       || null,
          ws:       item?.ecmwf_ws       || null,
        });
      } catch (error) {
        console.error('Failed to load forecast update dates:', error);
      }
    };
    fetchUpdateDates();
  }, [backendUrl]);

  // ── Helper: resolve base date for a spatial parameter ─────────────────────
  const getBaseDateForParameter = useCallback((paramId) => {
    if (paramId === 'rainfall' && forecastUpdateDates.rainfall) return forecastUpdateDates.rainfall;
    if (paramId === 'humidity' && forecastUpdateDates.rh)       return forecastUpdateDates.rh;
    if (paramId === 'wind_speed' && forecastUpdateDates.ws)     return forecastUpdateDates.ws;
    if (paramId === 'rainfall' && !hasDoneInitialFallback) {
      const utcNow   = new Date();
      const yesterday = new Date(utcNow.getTime() - 24 * 60 * 60 * 1000);
      const yyyy = yesterday.getUTCFullYear();
      const mm   = String(yesterday.getUTCMonth() + 1).padStart(2, '0');
      const dd   = String(yesterday.getUTCDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    return null;
  }, [forecastUpdateDates, hasDoneInitialFallback]);

  // ─────────────────────────────────────────────────────────────────────────
  // PARAMETER DEFINITIONS
  // ─────────────────────────────────────────────────────────────────────────
  const parameters = [
    {
      id:        'rainfall',
      label:     'Rainfall',
      icon:      CloudRain,
      color:     'text-blue-600',
      unit:      'mm',
      bgColor:   { backgroundColor: 'rgba(3, 5, 26, 0.6)'    },
      hoverColor:{ backgroundColor: 'rgba(59, 130, 246, 0.7)' },
      gradient:  { backgroundImage: 'linear-gradient(to right, #bfdbfe, #3b82f6, #1e3a8a)' },
      ranges: buildLegendRanges('rainfall'),
      legendImage: (() => {
        const date = getBaseDateForParameter('rainfall');
        if (!date) return '';
        const [y, m, d] = date.split('-');
        return `${ecmwfSpatialData}rainfall/${y}${m}${d}/legend_rain_accu_on_${y}-${m}-${d}_UTC00_d_00.svg`;
      })(),
    },
    {
      id:        'max_temp',
      label:     'Max Temperature',
      icon:      Thermometer,
      color:     'text-red-600',
      unit:      '°C',
      bgColor:   { backgroundColor: 'rgba(220, 38, 38, 0.6)'  },
      hoverColor:{ backgroundColor: 'rgba(220, 38, 38, 0.7)'  },
      gradient:  { backgroundImage: 'linear-gradient(to right, #fef9c3, #f97316, #991b1b)' },
      ranges: buildLegendRanges('max_temp'),
    },
    {
      id:        'min_temp',
      label:     'Min Temperature',
      icon:      Snowflake,
      color:     'text-blue-500',
      unit:      '°C',
      bgColor:   { backgroundColor: 'rgba(59, 130, 246, 0.4)' },
      hoverColor:{ backgroundColor: 'rgba(59, 130, 246, 0.5)' },
      gradient:  { backgroundImage: 'linear-gradient(to right, #dbeafe, #93c5fd, #2563eb)' },
      ranges: buildLegendRanges('min_temp'),
    },
    {
      id:        'humidity',
      label:     'Temperature Humidity Index',
      icon:      Droplets,
      color:     'text-teal-600',
      unit:      '°C',
      bgColor:   { backgroundColor: 'rgba(20, 184, 166, 0.6)' },
      hoverColor:{ backgroundColor: 'rgba(20, 184, 166, 0.7)' },
      gradient:  { backgroundImage: 'linear-gradient(to right, #99f6e4, #14b8a6, #0f766e)' },
      ranges: buildLegendRanges('humidity'),
      legendImage: (() => {
        const date = getBaseDateForParameter('humidity');
        if (!date) return '';
        const [y, m, d] = date.split('-');
        return `${ecmwfSpatialData}rhavg/${y}${m}${d}/legend_rhavg_on_${y}-${m}-${d}_UTC00_d_00.svg`;
      })(),
    },
    {
      id:        'wind_speed',
      label:     'Wind Speed',
      icon:      Wind,
      color:     'text-gray-600',
      unit:      'km/h',
      bgColor:   { backgroundColor: 'rgba(107, 114, 128, 0.6)' },
      hoverColor:{ backgroundColor: 'rgba(107, 114, 128, 0.7)' },
      gradient:  { backgroundImage: 'linear-gradient(to right, #e5e7eb, #6b7280, #1f2937)' },
      ranges: buildLegendRanges('wind_speed'),
      legendImage: (() => {
        const date = getBaseDateForParameter('wind_speed');
        if (!date) return '';
        const [y, m, d] = date.split('-');
        return `${ecmwfSpatialData}ws/${y}${m}${d}/legend_ws_on_${y}-${m}-${d}_UTC00_d_00.svg`;
      })(),
    },
    // ── NEW: Relative Humidity ─────────────────────────────────────────────
    {
      id:        'relative_humidity',
      label:     'Relative Humidity',
      icon:      ThermometerSun,
      color:     'text-cyan-600',
      unit:      '%',
      bgColor:   { backgroundColor: 'rgba(6, 182, 212, 0.6)' },
      hoverColor:{ backgroundColor: 'rgba(6, 182, 212, 0.7)' },
      gradient:  { backgroundImage: 'linear-gradient(to right, #cffafe, #06b6d4, #0e7490)' },
      ranges: buildLegendRanges('relative_humidity'),
    },
  ];

  // wind_speed used to be spatial-tab-only (raster tiles from the ecmwf app)
  // because the admin-level (suco/submunicipality/municipality/value-added)
  // tabs had no wind entry in variableMap - now that they do (sourced from
  // forecastmodels' 'wind' variable, same as every other admin-level
  // parameter), wind_speed belongs on every tab, not just spatial.
  const filteredParameters = activeToken === 'spatial'
    ? parameters.filter(p => ['rainfall', 'humidity', 'wind_speed'].includes(p.id))
    : parameters;

  const currentParameter = filteredParameters.find(p => p.id === selectedParameter) || filteredParameters[0];

  // ── Fetch static GeoJSON boundaries ───────────────────────────────────────
  const fetchStaticGeoJSON = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch('/DATA/OSMbased_adm1_boundary.json'),
        fetch('/DATA/OSMbased_adm2_boundary.json'),
        fetch('/DATA/OSMbased_adm3_boundary.json'),
        fetch('/DATA/OSMbased_adm0_boundary.json'),
      ]);
      if (!r1.ok || !r2.ok || !r3.ok || !r4.ok) throw new Error('Failed to fetch GeoJSON data');
      const [d1, d2, d3] = await Promise.all([r1.json(), r2.json(), r3.json(), r4.json()]);
      setMapData(d1);
      setMapDataSM(d2);
      setMapDataSuco(d3);
    } catch (error) {
      console.error('Error fetching static GeoJSON data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStaticGeoJSON(); }, [fetchStaticGeoJSON]);

  const cleanGeoJSON = (data) => {
    if (!data?.features) return data;
    return {
      ...data,
      features: data.features.map((feature) => {
        if (
          feature.geometry?.type === 'MultiPolygon' &&
          Array.isArray(feature.geometry.coordinates?.[0]?.[0]?.[0]?.[0])
        ) {
          return {
            ...feature,
            geometry: {
              ...feature.geometry,
              coordinates: feature.geometry.coordinates.map((poly) => poly[0]),
            },
          };
        }
        return feature;
      }),
    };
  };

  const spatialForecastDates = useMemo(() => {
    const utcNow  = new Date();
    const utcHour = utcNow.getUTCHours();
    const utcMin  = utcNow.getUTCMinutes();
    const startDate = (utcHour < 9 || (utcHour === 9 && utcMin < 30))
      ? new Date(utcNow.getTime() - 24 * 60 * 60 * 1000)
      : utcNow;
    return Array.from({ length: 10 }, (_, i) => {
      const fd   = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      const yyyy = fd.getUTCFullYear();
      const mm   = String(fd.getUTCMonth() + 1).padStart(2, '0');
      const dd   = String(fd.getUTCDate()).padStart(2, '0');
      return {
        day:      fd.getUTCDate(),
        month:    fd.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }),
        fullDate: fd.toLocaleString('en-US', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
        apiDate:  `${yyyy}-${mm}-${dd}_d_${i.toString().padStart(2, '0')}`,
      };
    });
  }, []);

  // ── Fetch non-spatial data when tab changes ────────────────────────────────
  useEffect(() => {
    setCurrentDay(0);
    setCurrentParameterIndex(0);
    setIsWeatherDataLoaded(false);

    if (['submunicipality', 'municipality', 'suco', 'valueAddedForecast'].includes(activeToken)) {
      const adminLevel = getAdminLevel(activeToken);
      if (!adminLevel) {
        setIsWeatherDataLoaded(true);
        return;
      }

      // Value Added tab reads the human-edited forecast_value_added figures;
      // every other admin-level tab reads the raw aggregate_value.
      const alertEndpoint = activeToken === 'valueAddedForecast' ? 'forecastvalueadded' : 'get-alert';

      const fetchNonSpatialData = async () => {
        setLoading(true);
        const results = {};
        try {
          const fetched = await Promise.all(
            Object.entries(variableMap).map(([paramId, varName]) =>
              fetch(`${backendUrl}forecastmodels/${alertEndpoint}/?variable=${varName}&admin_level=${adminLevel}`)
                .then(r => { if (!r.ok) throw new Error(`Failed for ${varName}`); return r.json(); })
                .then(data => ({ paramId, varName, data }))
                .catch(err => { console.error(err); return null; })
            )
          );

          let sourceSet = false;
          fetched.forEach(result => {
            if (!result) return;
            const { paramId, data } = result;
            if (data.data) {
              results[paramId] = { ...data, data: data.data };
              if (!sourceSet && data?.source) {
                setDataSource(data.source);
                sourceSet = true;
              }
            }
          });

          setNonSpatialData(results);

          const firstParamKey = Object.keys(results)[0];
          if (firstParamKey && results[firstParamKey]?.data) {
            const sampleAdmKey = Object.keys(results[firstParamKey].data)[0];
            if (sampleAdmKey) {
              const periods = results[firstParamKey].data[sampleAdmKey];
              setForecastDates(
                periods.map((period) => {
                  const d = new Date(period.period_start);
                  return {
                    day:      d.getDate(),
                    month:    d.toLocaleString('en-US', { month: 'short' }),
                    fullDate: d.toLocaleString('en-US', { day: 'numeric', month: 'short' }),
                  };
                })
              );
            }
          }
          setIsWeatherDataLoaded(true);
        } catch (error) {
          console.error(`Error fetching ${activeToken} data:`, error);
          setNonSpatialData({});
          setForecastDates([]);
          setIsWeatherDataLoaded(true);
        } finally {
          setLoading(false);
        }
      };

      fetchNonSpatialData();
    } else if (activeToken === 'spatial') {
      setNonSpatialData({});
      setDataSource('ECMWF');
      setForecastDates(spatialForecastDates);
      setSelectedParameter('rainfall');
      setCurrentParameterIndex(0);
      setIsWeatherDataLoaded(true);
    } else {
      setForecastDates([]);
      setIsWeatherDataLoaded(true);
    }
  }, [activeToken, getAdminLevel, variableMap, spatialForecastDates, backendUrl]);

  // ── Fetch spatial GeoJSON data ─────────────────────────────────────────────
  useEffect(() => {
    const fetchSpatialData = async () => {
      if (activeToken !== 'spatial' || !forecastDates[currentDay]) return;
      const baseDate = getBaseDateForParameter(selectedParameter);
      if (!baseDate) {
        console.warn(`No API date available for ${selectedParameter}. Skipping spatial data load.`);
        setRainfallData(null);
        setHeatIndexData(null);
        setWindSpeedData(null);
        setLoading(false);
        return;
      }
      if (selectedParameter === 'rainfall' && !forecastUpdateDates.rainfall) {
        setHasDoneInitialFallback(true);
      }
      setLoading(true);
      try {
        const paddedDay  = currentDay.toString().padStart(2, '0');
        const [year, month, date] = baseDate.split('-');
        const todayStr   = `${year}${month}${date}`;
        const todayDashed = `${year}-${month}-${date}`;
        let url = '';
        if (selectedParameter === 'rainfall') {
          url = `${ECMWF_SPATIAL_DATA}rainfall/${todayStr}/rain_accu_on_${todayDashed}_UTC00_d_${paddedDay}.geojson`;
        } else if (selectedParameter === 'humidity') {
          url = `${ECMWF_SPATIAL_DATA}rhavg/${todayStr}/rhavg_on_${todayDashed}_UTC00_d_${paddedDay}.geojson`;
        } else if (selectedParameter === 'wind_speed') {
          url = `${ECMWF_SPATIAL_DATA}ws/${todayStr}/ws_on_${todayDashed}_UTC00_d_${paddedDay}.geojson`;
        }
        if (!url) return;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch GeoJSON');
        const cleaned = cleanGeoJSON(await response.json());
        setRainfallData(null);
        setHeatIndexData(null);
        setWindSpeedData(null);
        if (selectedParameter === 'rainfall')        setRainfallData(cleaned);
        else if (selectedParameter === 'humidity')   setHeatIndexData(cleaned);
        else if (selectedParameter === 'wind_speed') setWindSpeedData(cleaned);
      } catch (err) {
        console.error('Error loading spatial data:', err);
        setRainfallData(null);
        setHeatIndexData(null);
        setWindSpeedData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSpatialData();
  }, [activeToken, currentDay, selectedParameter, forecastDates, ECMWF_SPATIAL_DATA, forecastUpdateDates, hasDoneInitialFallback, getBaseDateForParameter]);

  // ── Playback interval ──────────────────────────────────────────────────────
  useEffect(() => {
    let interval;
    if (isPlaying && forecastDates.length > 0) {
      interval = setInterval(() => {
        setCurrentDay((prevDay) => {
          const nextDay = (prevDay + 1) % forecastDates.length;
          if (isLoopingTabs && nextDay === 0) {
            const nextParamIndex = (currentParameterIndex + 1) % filteredParameters.length;
            if (nextParamIndex === 0) {
              const currentTabIndex = tabs.findIndex((tab) => tab.id === activeToken);
              let nextTabIndex = (currentTabIndex + 1) % tabs.length;
              // Satellite Imagery has no forecast days/params to loop through — skip it.
              while (tabs[nextTabIndex].id === 'satelliteImagery') {
                nextTabIndex = (nextTabIndex + 1) % tabs.length;
              }
              setActiveTab(tabs[nextTabIndex].id);
              setCurrentParameterIndex(0);
              setSelectedParameter(tabs[nextTabIndex].id === 'spatial' ? 'rainfall' : filteredParameters[0].id);
            } else {
              setCurrentParameterIndex(nextParamIndex);
              setSelectedParameter(filteredParameters[nextParamIndex].id);
            }
          }
          return nextDay;
        });
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, forecastDates.length, isLoopingTabs, activeToken, currentParameterIndex, tabs, filteredParameters]);

  // ── Lock dashboard scroll while the data table modal is open ──────────────
  useEffect(() => {
    const shouldLock = isStatsOpen && !['spatial', 'satelliteImagery'].includes(activeToken);
    document.body.style.overflow = shouldLock ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isStatsOpen, activeToken]);

  const togglePlay = () => {
    setIsPlaying((prev) => {
      const next = !prev;
      if (next) setIsLoopingTabs(false);
      return next;
    });
  };

  const toggleLoopTabs = () => {
    setIsLoopingTabs((prev) => {
      const next = !prev;
      setIsPlaying(next);
      if (next) {
        setActiveTab('spatial');
        setCurrentParameterIndex(0);
        setSelectedParameter('rainfall');
        setCurrentDay(0);
      }
      return next;
    });
  };

  useEffect(() => {
    if (forecastDates.length > 0 && currentDay >= forecastDates.length) setCurrentDay(0);
  }, [forecastDates, currentDay]);

  useEffect(() => {
    if (!filteredParameters.find((p) => p.id === selectedParameter)) {
      setSelectedParameter(filteredParameters[0]?.id || 'rainfall');
      setCurrentParameterIndex(0);
    }
  }, [activeToken, filteredParameters, selectedParameter]);

  const handleUserInteraction = () => {
    if (isPlaying || isLoopingTabs) {
      setIsPlaying(false);
      setIsLoopingTabs(false);
      setTimeout(() => {
        setIsPlaying(true);
        setIsLoopingTabs(true);
      }, 10000);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // MAP STYLE FUNCTIONS
  // ─────────────────────────────────────────────────────────────────────────

  const spatialStyle = (feature) => ({
    fillColor:   feature.properties.fill               || '#a79d73',
    fillOpacity: feature.properties['fill-opacity']    || 1,
    color:       feature.properties.stroke             || '#a79d73',
    opacity:     feature.properties['stroke-opacity']  || 1,
    weight:      feature.properties['stroke-width']    || 0,
  });

  // Non-spatial: min_temp/max_temp → PARAM_RANGES; everything else → API color[0]
  const getNonSpatialStyle = (feature) => {
    const defaultStyle = {
      fillColor:   ALERT_COLORS.grey,
      color:       'black',
      weight:      1,
      fillOpacity: 1,
      opacity:     1,
    };

    const paramData = nonSpatialData[selectedParameter];
    if (!paramData?.data) return defaultStyle;

    const admCode    = feature.properties.ADM_Code?.toString();
    const admRecords = paramData.data[admCode];
    if (!Array.isArray(admRecords) || !admRecords[currentDay]) return defaultStyle;

    const record = admRecords[currentDay];
    const value = record.aggregate_value;
    if (value == null) return defaultStyle;

    return {
      fillColor:   getColorFromRanges(selectedParameter, Number(value)),
      color:       'black',
      weight:      1,
      fillOpacity: 1,
      opacity:     1,
    };
  };

  const municipalityStyle    = getNonSpatialStyle;
  const subMunicipalityStyle = getNonSpatialStyle;
  const sucoStyle            = getNonSpatialStyle;

  const handleSliderClick = (e) => {
    if (!forecastDates.length) return;
    const rect       = e.currentTarget.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    const dayIndex   = Math.round(percentage * (forecastDates.length - 1));
    setCurrentDay(Math.max(0, Math.min(dayIndex, forecastDates.length - 1)));
  };

  const handleParameterClick = (paramId) => {
    setSelectedParameter(paramId);
    setCurrentParameterIndex(filteredParameters.findIndex((p) => p.id === paramId));
    handleUserInteraction();
  };

  const getFeatureValue = (admCode) => {
    if (activeToken === 'spatial' || !nonSpatialData[selectedParameter] || !forecastDates.length || currentDay >= forecastDates.length) return null;
    const paramData = nonSpatialData[selectedParameter];
    if (!paramData?.data) return null;
    const admRecords = paramData.data[admCode?.toString()];
    if (!Array.isArray(admRecords) || currentDay >= admRecords.length) return null;
    const dayData = admRecords[currentDay];
    return dayData?.aggregate_value ?? null;
  };

  const onEachFeature = (feature, layer, level) => {
    if (!feature.properties) return;
    if (level === 'spatial') {
      layer.bindTooltip(feature.properties.title || 'N/A', {
        permanent: false, sticky: true, direction: 'auto', className: 'spatial-tooltip',
      });
      layer.options.interactive = true;
      layer.on({
        click: (e) => {
          L.DomEvent.preventDefault(e);
          L.DomEvent.stopPropagation(e);
          layer.setStyle({
            color:   feature.properties.stroke             || '#a79d73',
            weight:  feature.properties['stroke-width']    || 0,
            opacity: feature.properties['stroke-opacity']  || 1,
          });
        },
      });
    } else if (level !== 'adm0') {
      const placeName     = feature.properties[level] || 'Unknown';
      const paramValueRaw = getFeatureValue(feature.properties.ADM_Code);
      const displayValue  = paramValueRaw !== null ? `${paramValueRaw} ${currentParameter.unit}` : 'N/A';
      layer.bindTooltip(`${placeName} - ${currentParameter.label}: ${displayValue}`, {
        permanent: false, sticky: true, direction: 'auto', offset: [0, 0], className: 'custom-tooltip',
      });

      // Breadcrumb of admin names down to the clicked level (Municipality › Sub Municipality › Suco)
      const breadcrumbParts = [];
      if (feature.properties.ADM1) breadcrumbParts.push(feature.properties.ADM1);
      if (level !== 'ADM1' && feature.properties.ADM2) breadcrumbParts.push(feature.properties.ADM2);
      if (level === 'ADM3' && feature.properties.ADM3) breadcrumbParts.push(feature.properties.ADM3);
      const breadcrumb = breadcrumbParts.join(' &rsaquo; ');

      // Per-region forecast table: one row per forecast date for the selected parameter
      const admCode    = feature.properties.ADM_Code?.toString();
      const admRecords = nonSpatialData[selectedParameter]?.data?.[admCode] || [];
      const rowsHtml = forecastDates.map((d, idx) => {
        const val    = admRecords[idx]?.aggregate_value;
        const hasVal = val !== null && val !== undefined;
        const bg     = hasVal ? getColorFromRanges(selectedParameter, Number(val)) : ALERT_COLORS.grey;
        return `<tr>
            <td style="padding:3px 6px;border:1px solid #e5e7eb;">${d.fullDate}</td>
            <td style="padding:3px 6px;border:1px solid #e5e7eb;text-align:center;background:${bg};font-weight:600;">${hasVal ? fmtVal(val) : '-'}</td>
          </tr>`;
      }).join('');

      layer.bindPopup(
        `<div style="font-size:12px;color:#333;min-width:190px;">
           <h3 style="margin:0 0 4px;font-size:14px;color:#2563eb;">${placeName}</h3>
           ${breadcrumb ? `<div style="font-size:10.5px;color:#6b7280;margin-bottom:6px;">${breadcrumb}</div>` : ''}
           <table style="border-collapse:collapse;width:100%;font-size:11px;">
             <thead>
               <tr>
                 <th style="padding:3px 6px;border:1px solid #e5e7eb;background:#f1f5f9;text-align:left;">Date</th>
                 <th style="padding:3px 6px;border:1px solid #e5e7eb;background:#f1f5f9;">${currentParameter.label} (${currentParameter.unit})</th>
               </tr>
             </thead>
             <tbody>${rowsHtml || `<tr><td colspan="2" style="padding:6px;text-align:center;color:#9ca3af;border:1px solid #e5e7eb;">No data</td></tr>`}</tbody>
           </table>
         </div>`,
        { maxWidth: 280, className: 'custom-popup' }
      );
      layer.on({
        click: (e) => {
          layer.setStyle({ color: 'black', weight: 1, dashArray: '', opacity: 1 });
          layer.openPopup();
          e.originalEvent.preventDefault();
          L.DomEvent.stopPropagation(e);
        },
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // DATA TABLE (replaces the old alert-count stats popup)
  // Builds one row per admin unit for the active admin-level tab, with a
  // column per forecast date holding the currently selected parameter's value.
  // ─────────────────────────────────────────────────────────────────────────
  const dataTableGeo = activeToken === 'municipality'
    ? mapData
    : activeToken === 'submunicipality'
    ? mapDataSM
    : (activeToken === 'suco' || activeToken === 'valueAddedForecast')
    ? mapDataSuco
    : null;

  const dataTableRows = useMemo(() => {
    if (!dataTableGeo?.features) return [];
    const paramData = nonSpatialData[selectedParameter]?.data;
    const byCode = new Map();
    dataTableGeo.features.forEach((f) => {
      const admCode = f.properties?.ADM_Code?.toString();
      if (!admCode || byCode.has(admCode)) return;
      byCode.set(admCode, f.properties);
    });
    const rows = [];
    byCode.forEach((props, admCode) => {
      const records = paramData?.[admCode] || [];
      const row = {
        admCode,
        municipality:    props.ADM1 || '-',
        subMunicipality: props.ADM2 || '-',
        suco:            props.ADM3 || '-',
      };
      forecastDates.forEach((_, dayIdx) => {
        row[`day_${dayIdx}`] = records[dayIdx]?.aggregate_value ?? null;
      });
      rows.push(row);
    });
    return rows;
  }, [dataTableGeo, nonSpatialData, selectedParameter, forecastDates]);

  const dataTableColumns = useMemo(() => {
    const cols = [
      { key: 'SlNo', label: 'S.No', sortable: false, align: 'center' },
      { key: 'municipality', label: 'Municipality', sortable: true, align: 'left' },
    ];
    if (activeToken === 'submunicipality' || activeToken === 'suco' || activeToken === 'valueAddedForecast') {
      cols.push({ key: 'subMunicipality', label: 'Sub Municipality', sortable: true, align: 'left' });
    }
    if (activeToken === 'suco' || activeToken === 'valueAddedForecast') {
      cols.push({ key: 'suco', label: 'Suco', sortable: true, align: 'left' });
    }
    forecastDates.forEach((date, dayIdx) => {
      cols.push({
        key: `day_${dayIdx}`,
        label: date.fullDate,
        // Value Added tab: day columns are sortable so the forecast value
        // can be re-sorted per day; other tabs keep the original stable order.
        sortable: activeToken === 'valueAddedForecast',
        align: 'center',
        render: (row) => {
          const val = row[`day_${dayIdx}`];
          const hasVal = val !== null && val !== undefined;
          const bg = hasVal ? getColorFromRanges(selectedParameter, Number(val)) : ALERT_COLORS.grey;
          return (
            <span
              style={{
                display: 'inline-block', minWidth: '38px', padding: '3px 8px',
                borderRadius: '6px', fontWeight: 600, fontSize: '12px',
                backgroundColor: bg,
              }}
            >
              {hasVal ? fmtVal(val) : '-'}
            </span>
          );
        },
      });
    });
    return cols;
  }, [activeToken, forecastDates, selectedParameter]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fm_forecast-map-container" style={{ marginBottom: '20px' }}>
      <div style={{ position: 'relative', backgroundColor: 'white', borderRadius: '1rem', overflow: 'hidden' }}>

        {/* ── Tab Bar ─────────────────────────────────────────────────────── */}
        <div
          className="dashboardtabscontainer fm_dashboard-tabs"
          style={{
            position: 'relative', zIndex: 1000,
            backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(4px)',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0 1rem',
          }}
        >
          <div style={{ display: 'flex' }}>
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setCurrentDay(0);
                  setCurrentParameterIndex(0);
                  setSelectedParameter(tab.id === 'spatial' ? 'rainfall' : filteredParameters[0].id);
                  if (['municipality', 'submunicipality', 'suco', 'valueAddedForecast', 'spatial'].includes(tab.id)) setLoading(true);
                  if (tab.id === 'spatial') setIsStatsOpen(false);
                  handleUserInteraction();
                }}
                style={{
                  padding: '0.75rem 2rem', fontSize: '0.875rem', fontWeight: 600,
                  transition: 'all 0.3s', position: 'relative',
                  color:           activeToken === tab.id ? '#2563eb' : '#4b5563',
                  backgroundColor: activeToken === tab.id ? 'white' : 'transparent',
                  borderTopLeftRadius:  index === 0              ? '1rem' : '0',
                  borderTopRightRadius: index === tabs.length - 1 ? '1rem' : '0',
                  border: 'none', cursor: 'pointer',
                }}
                onMouseEnter={(e) => { if (activeToken !== tab.id) { e.currentTarget.style.color = '#111827'; e.currentTarget.style.backgroundColor = 'rgba(243,244,246,0.5)'; }}}
                onMouseLeave={(e) => { if (activeToken !== tab.id) { e.currentTarget.style.color = '#4b5563'; e.currentTarget.style.backgroundColor = 'transparent'; }}}
              >
                {tab.label}
                {activeToken === tab.id && (
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', backgroundColor: '#2563eb' }} />
                )}
              </button>
            ))}
          </div>

          {/* Source badge + Stats + Loop buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ fontSize: '0.6rem', fontWeight: 500, color: '#000', backgroundColor: '#f9f7f7', borderRadius: '18px', padding: '0.3rem 1rem', display: 'flex', alignItems: 'center' }}>
              {activeToken === 'spatial'
                ? 'Source: ECMWF - Data will update daily at 9AM (UTC)'
                : activeToken === 'satelliteImagery'
                ? 'Source: Himawari-9 - MetStudio'
                : `Source: ${dataSource} - CDIS`}
            </div>
            {!['spatial', 'satelliteImagery'].includes(activeToken) && (
              <div
                onClick={() => setIsStatsOpen(true)}
                title="View data table"
                style={{ width: '2rem', height: '2rem', borderRadius: '9999px', backgroundColor: '#f9f7f7', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background-color 0.3s' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e5e7eb')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f9f7f7')}
              >
                <TableIcon size={16} style={{ color: '#374151' }} />
              </div>
            )}
            <button
              onClick={toggleLoopTabs}
              style={{ width: '2.2rem', height: '2.2rem', backgroundColor: isLoopingTabs ? '#2563eb' : '#6b7280', color: 'white', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s', border: 'none', flexShrink: 0, cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isLoopingTabs ? '#1d4ed8' : '#4b5563')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isLoopingTabs ? '#2563eb' : '#6b7280')}
            >
              <Repeat size={16} />
            </button>
          </div>
        </div>

        {/* ── Map Container ────────────────────────────────────────────────── */}
        <div className="dashboardforecastmapcontainer fm_map-container" style={{ position: 'relative', height: '72vh' }}>
          {activeToken === 'satelliteImagery' ? (
            <SatelliteImagery embedded />
          ) : (
          <>
          <MapContainer
            className="map-forecast-container"
            zoom={9} center={[-8.874217, 125.727539]}
            zoomControl={false} scrollWheelZoom={true} doubleClickZoom={false}
            style={{ height: '100%', width: '100%', zIndex: 1 }}
          >
            <MapAutoResize />
            <Pane name="contourPane" style={{ zIndex: 250 }} />
            <Pane name="timorPane"   style={{ zIndex: 400 }} />

            {['municipality', 'submunicipality', 'suco', 'valueAddedForecast'].includes(activeToken) && (
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            )}

            {activeToken === 'spatial' && rainfallData && selectedParameter === 'rainfall' && (
              <GeoJSON key={`rainfall-${currentDay}-${Date.now()}`}  data={rainfallData}  pane="contourPane" style={spatialStyle} onEachFeature={(f, l) => onEachFeature(f, l, 'spatial')} />
            )}
            {activeToken === 'spatial' && heatIndexData && selectedParameter === 'humidity' && (
              <GeoJSON key={`heatindex-${currentDay}-${Date.now()}`} data={heatIndexData} pane="contourPane" style={spatialStyle} onEachFeature={(f, l) => onEachFeature(f, l, 'spatial')} />
            )}
            {activeToken === 'spatial' && windSpeedData && selectedParameter === 'wind_speed' && (
              <GeoJSON key={`windspeed-${currentDay}-${Date.now()}`} data={windSpeedData} pane="contourPane" style={spatialStyle} onEachFeature={(f, l) => onEachFeature(f, l, 'spatial')} />
            )}

            {activeToken === 'spatial' && mapDataSuco && (
              <GeoJSON
                key="suco-base-spatial"
                data={mapDataSuco}
                pane="timorPane"
                style={() => ({ fillColor: 'transparent', fillOpacity: 0, color: '#374151', weight: 2.5, opacity: 0.9 })}
                onEachFeature={(_, layer) => { layer.options.interactive = false; }}
              />
            )}

            {activeToken === 'municipality'    && mapData     && isWeatherDataLoaded && (
              <GeoJSON key={`municipality-${selectedParameter}-${currentDay}`}    data={mapData}     style={municipalityStyle}    onEachFeature={(f, l) => onEachFeature(f, l, 'ADM1')} />
            )}
            {activeToken === 'submunicipality' && mapDataSM   && isWeatherDataLoaded && (
              <GeoJSON key={`submunicipality-${selectedParameter}-${currentDay}`} data={mapDataSM}   style={subMunicipalityStyle} onEachFeature={(f, l) => onEachFeature(f, l, 'ADM2')} />
            )}
            {activeToken === 'suco'            && mapDataSuco && isWeatherDataLoaded && (
              <GeoJSON key={`suco-${selectedParameter}-${currentDay}`}            data={mapDataSuco} style={sucoStyle}            onEachFeature={(f, l) => onEachFeature(f, l, 'ADM3')} />
            )}
            {activeToken === 'valueAddedForecast' && mapDataSuco && isWeatherDataLoaded && (
              <GeoJSON key={`valueAddedForecast-${selectedParameter}-${currentDay}`} data={mapDataSuco} style={sucoStyle} onEachFeature={(f, l) => onEachFeature(f, l, 'ADM3')} />
            )}

            {loading && (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <Mainloader />
              </div>
            )}
          </MapContainer>

          {/* ── Parameter selector (right side) ────────────────────────────── */}
          <div style={{ position: 'absolute', top: '1rem', right: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', zIndex: 1000 }}>
            {filteredParameters.map((param) => {
              const IconComponent = param.icon;
              const isSelected    = selectedParameter === param.id;
              const isHovered     = hoveredParameter  === param.id;
              return (
                <div
                  key={param.id}
                  onClick={() => handleParameterClick(param.id)}
                  onMouseEnter={() => setHoveredParameter(param.id)}
                  onMouseLeave={() => setHoveredParameter(null)}
                  style={{
                    width: '3rem', height: '3rem', borderRadius: '9999px',
                    backdropFilter: 'blur(4px)',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                    border:           isSelected ? '2px solid #3b82f6' : '1px solid rgba(229,231,235,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.3s',
                    transform:        isSelected || isHovered ? 'scale(1.05)' : 'scale(1)',
                    backgroundColor:  isSelected ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)',
                    zIndex: 1000, position: 'relative',
                  }}
                >
                  <div style={{ width: '1.5rem', height: '1.5rem', borderRadius: '9999px', ...param.bgColor, ...(isHovered && param.hoverColor), transition: 'background-color 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconComponent size={14} style={{ color: 'white' }} />
                  </div>
                  {isHovered && (
                    <div className="fm_param-tooltip" style={{ position: 'absolute', right: '3.5rem', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgb(255,255,255)', color: 'blue', padding: '0.25rem 0.5rem', borderRadius: '0.5rem', fontSize: '0.75rem', whiteSpace: 'nowrap', zIndex: 1001, animation: 'slideIn 0.3s ease-out' }}>
                      {param.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Legend (bottom-right) ─────────────────────────────────────── */}
          <div style={{ position: 'absolute', bottom: '0.2rem', right: '0rem', display: 'flex', flexDirection: 'column', padding: '2px 4px', backgroundColor: 'white', borderRadius: '8px', zIndex: 1000, textAlign: 'right' }}>
            <div style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', fontWeight: 500, color: '#374151', marginBottom: '0.1rem' }}>
              {currentParameter.label} ({currentParameter.unit})
            </div>

            {activeToken === 'spatial' ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                {['rainfall', 'humidity', 'wind_speed'].includes(selectedParameter) && currentParameter.legendImage ? (
                  <img
                    src={currentParameter.legendImage}
                    alt={`${currentParameter.label} legend`}
                    style={{ width: '17rem', height: '2rem', objectFit: 'contain', borderRadius: '0.25rem' }}
                  />
                ) : (
                  <div style={{ width: '8rem', height: '0.5rem', borderRadius: '0.25rem', ...currentParameter.gradient }} />
                )}
                {!(['rainfall', 'humidity', 'wind_speed'].includes(selectedParameter)) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '8rem', fontSize: '0.6rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    {currentParameter.ranges.map((r, i) => (
                      <div key={i} style={{ textAlign: 'center', flex: 1 }}>{r.value}</div>
                    ))}
                  </div>
                )}
              </div>
            ) : ['municipality', 'submunicipality', 'suco', 'valueAddedForecast'].includes(activeToken) ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                {currentParameter.ranges.map((r, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: '1.625rem', height: '0.5rem', borderRadius: '0.25rem', ...r.color }} />
                    <div style={{ fontSize: '0.55rem', color: '#374151', marginTop: '0.15rem', textAlign: 'center', fontWeight: 600 }}>
                      {r.label}
                    </div>
                    <div style={{ fontSize: '0.5rem', color: '#6b7280', textAlign: 'center' }}>
                      {r.value}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* ── Data Table Modal (suco / sub-municipality / municipality) ─────── */}
          {isStatsOpen && !['spatial', 'satelliteImagery'].includes(activeToken) && (
            <div
              className="fm_stats-modal-overlay"
              style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <div
                className="fm_stats-modal"
                style={{ backgroundColor: 'white', borderRadius: '1rem', width: '1400px', maxWidth: '95vw', height: '75vh', position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              >
                <button
                  onClick={() => setIsStatsOpen(false)}
                  style={{ position: 'absolute', top: '0.6rem', right: '0.9rem', background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#374151' }}
                >
                  ×
                </button>
                <div style={{ padding: '1.5rem 1.5rem 0', flexShrink: 0 }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 600, margin: 0, color: '#2563eb' }}>
                    {currentParameter.label} Forecast — {tabs.find((t) => t.id === activeToken)?.label || ''} Data Table
                  </h2>
                </div>
                <div style={{ flex: 1, minHeight: 0, paddingBottom: '1.5rem', boxSizing: 'border-box' }}>
                  <CommonTable
                    tableData={dataTableRows}
                    columns={dataTableColumns}
                    tableName={`Forecast_${activeToken}_${selectedParameter}`}
                    searchableKeys={['municipality', 'subMunicipality', 'suco']}
                    searchPlaceholder="Search by municipality, sub municipality, or suco..."
                    emptyMessage="No forecast data available for this selection."
                    defaultSort={activeToken === 'valueAddedForecast' ? { key: 'day_0', direction: 'desc' } : null}
                    rowIdKey="admCode"
                    scrollableBody
                  />
                </div>
              </div>
            </div>
          )}
          </>
          )}
        </div>

        {/* ── Timeline Slider ───────────────────────────────────────────────── */}
        {activeToken !== 'satelliteImagery' && (
        <div
          className="dashboardslider fm_slider-container"
          style={{ backgroundColor: 'white', borderTop: '1px solid #e5e7eb', padding: '0.3rem 0.4rem', zIndex: 100 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={togglePlay}
              style={{ width: '2.2rem', height: '2.2rem', backgroundColor: '#2563eb', color: 'white', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s', border: 'none', flexShrink: 0, cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1d4ed8')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2563eb')}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ position: 'relative', marginTop: '10px' }}>
                <div
                  style={{ width: '100%', height: '0.5rem', backgroundColor: '#e5e7eb', borderRadius: '9999px', cursor: forecastDates.length ? 'pointer' : 'default', position: 'relative', overflow: 'hidden' }}
                  onClick={handleSliderClick}
                >
                  {forecastDates.length > 0 && (
                    <>
                      <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', backgroundColor: '#2563eb', borderRadius: currentDay === 0 ? '9999px' : '9999px 0 0 9999px', width: `${(currentDay / (forecastDates.length - 1)) * 100}%`, transition: 'width 0.3s ease' }} />
                      <div style={{ position: 'absolute', top: '50%', left: `calc(${(currentDay / (forecastDates.length - 1)) * 100}%)`, transform: 'translate(-50%,-50%)', width: '1.5rem', height: '1.5rem', backgroundColor: '#2563eb', borderRadius: '50%', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 2, transition: 'left 0.3s ease' }} />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 0.25rem' }}>
                        {forecastDates.map((_, index) => (
                          <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ width: '2px', height: '1rem', backgroundColor: index <= currentDay ? 'white' : '#9ca3af', transition: 'background-color 0.2s' }} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', padding: '0 0.25rem' }}>
                  {forecastDates.map((date, index) => (
                    <span key={index} style={{ fontSize: '0.65rem', transition: 'color 0.2s', color: index === currentDay ? '#2563eb' : '#6b7280', fontWeight: index === currentDay ? 500 : 400 }}>
                      {date.fullDate}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(10px); }
          to   { opacity: 1; transform: translateX(0);    }
        }
        .leaflet-tooltip {
          background-color: rgba(255,255,255,0.9); border: 1px solid #ccc;
          border-radius: 4px; padding: 4px 8px; color: black;
          font-size: 10px; font-weight: bold; text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .leaflet-tooltip.custom-tooltip {
          background-color: rgba(255,255,255,0.9); border: 1px solid #ccc;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2); font-size: 10px;
          font-weight: bold; color: black; white-space: nowrap; width: auto;
        }
        .leaflet-tooltip.spatial-tooltip {
          background-color: rgba(255,255,255,0.9); border: 1px solid #ccc;
          border-radius: 4px; padding: 4px 8px; color: black; font-size: 10px;
          font-weight: bold; text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2); white-space: nowrap;
        }
        .leaflet-interactive:focus, .leaflet-interactive:active {
          outline: none !important; stroke: black !important; stroke-width: 1 !important;
        }
        .leaflet-pane > svg path.spatial-geojson { transition: none !important; }
        .fm_forecast-map-container { width: 100%; max-width: 100%; }
        @media (max-width: 1024px) {
          .fm_dashboard-tabs { flex-direction: column; align-items: flex-start; padding: 0.5rem; }
          .fm_dashboard-tabs > div:first-child { display: grid; grid-template-columns: repeat(2,1fr); gap: 0.25rem; width: 100%; }
          .fm_dashboard-tabs button { width: 100%; padding: 0.5rem 0.75rem; font-size: 0.75rem; border-radius: 0.5rem !important; margin-bottom: 0; }
          .fm_map-container { height: 60vh; }
          .fm_map-container img { width: 12rem; height: 1.5rem; }
          .fm_map-container > div:nth-child(2) { gap: 0.5rem; }
          .fm_map-container > div:nth-child(2) > div { width: 2.5rem; height: 2.5rem; }
          .fm_map-container > div:nth-child(2) > div > div { width: 1.2rem; height: 1.2rem; }
          .fm_map-container > div:nth-child(2) > div > .fm_param-tooltip { right: 3rem; font-size: 0.65rem; padding: 0.2rem 0.4rem; }
          .fm_stats-modal-overlay { align-items: flex-start; padding-top: 1rem; }
          .fm_stats-modal h2 { font-size: 1rem; }
        }
        @media (max-width: 768px) {
          .fm_dashboard-tabs > div:first-child { display: grid !important; grid-template-columns: repeat(2,1fr); gap: 0.2rem; width: 100%; flex-wrap: nowrap; }
          .fm_dashboard-tabs button { width: 100%; padding: 0.4rem 0.6rem; font-size: 0.7rem; border-radius: 0.4rem !important; box-sizing: border-box; }
          .fm_map-container { height: 50vh; }
          .fm_map-container img { width: 10rem; height: 1.2rem; }
          .fm_map-container > div:nth-child(2) { gap: 0.4rem; top: 0.5rem; right: 0.5rem; }
          .fm_map-container > div:nth-child(2) > div { width: 2rem; height: 2rem; }
          .fm_map-container > div:nth-child(2) > div > div { width: 1rem; height: 1rem; }
          .fm_map-container > div:nth-child(2) > div > .fm_param-tooltip { right: 2.5rem; font-size: 0.6rem; padding: 0.15rem 0.3rem; }
          .fm_stats-modal h2 { font-size: 0.9rem; }
        }
      `}</style>
    </div>
  );
};

export default ForecastMap;